<script setup lang="ts" generic="T extends number | string = number | string">
import { ref, computed } from 'vue';
import {
    BAutocomplete, BChip, BDivider, BListItem, BListItemTitle,
} from '@/design-system';
import { useTranslation } from '@/composables';

type ItemType<V = T> = {
    value: V;
    title: string;
};

type PropsType = {
    modelValue: T[] | undefined;
    items: ItemType<T>[];
    label: string;
    loading?: boolean;
    placeholder?: string;
    noFilter?: boolean;

    /** Offer a "select all / clear" toggle as the first item of the menu. */
    selectAll?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    loading: false,
    placeholder: '',
    noFilter: true,
    selectAll: false,
});

const emit = defineEmits<{
    'update:modelValue': [value: T[] | undefined];
    search: [query: string];
    menuOpen: [isOpen: boolean];
}>();

const searchQuery = ref('');
const { t } = useTranslation();

let searchTimeout: ReturnType<typeof setTimeout>;
const debouncedSearch = (query: string) => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        emit('search', query);
    }, 300);
};

const handleSearchUpdate = (query: string) => {
    searchQuery.value = query;
    debouncedSearch(query);
};

const handleMenuUpdate = (isOpen: boolean) => {
    emit('menuOpen', isOpen);
};

const handleValueUpdate = (value: T | T[] | null | undefined) => {
    if (value === null || value === undefined) {
        emit('update:modelValue', undefined);

        return;
    }

    if (Array.isArray(value)) {
        emit('update:modelValue', value.length > 0 ? value : undefined);
    }
};

const removeItem = (itemValue: T) => {
    if (!props.modelValue) {
        return;
    }

    const updatedValue = props.modelValue.filter(v => v !== itemValue);
    emit('update:modelValue', updatedValue.length > 0 ? updatedValue : undefined);
};

const truncateTitle = (title: string, maxLength: number = 15): string => {
    if (title.length <= maxLength) {
        return title;
    }

    return `${title.slice(0, maxLength)}...`;
};

const selectedCount = computed(() => props.modelValue?.length ?? 0);
const hasNoData = computed(() => props.items.length === 0 && !props.loading);

/**
 * Every offered item is picked. Compared by membership rather than by count,
 * because the offered list can narrow under a selection that was made against
 * a wider one - registers drop away when stores are filtered - and equal
 * lengths would then read as "all" while naming different things.
 */
const allSelected = computed(() => {
    if (props.items.length === 0) {
        return false;
    }

    const selected = new Set<T>(props.modelValue ?? []);

    return props.items.every(item => selected.has(item.value));
});

/** Nothing to select all of, so the toggle would be a dead row. */
const showSelectAll = computed(() => props.selectAll && props.items.length > 0);

/**
 * Clearing emits undefined rather than an empty array: callers store "nothing
 * picked" as undefined, and an empty array would read as a filter that matches
 * nothing. Selecting takes every item, not just the ones a typed query leaves
 * on screen - Vuetify keeps the filtered list to itself.
 */
const toggleAll = () => {
    emit('update:modelValue', allSelected.value ? undefined : props.items.map(item => item.value));
};
</script>

<template>
    <div class="b-filter-group">
        <BAutocomplete
            :model-value="modelValue"
            v-model:search="searchQuery"
            :items="items"
            :label="label"
            :loading="loading"
            :placeholder="placeholder"
            :no-filter="noFilter"
            multiple
            chips
            hide-details
            rounded="xl"
            density="compact"
            variant="outlined"
            class="b-filter-autocomplete"
            @update:model-value="handleValueUpdate"
            @update:search="handleSearchUpdate"
            @update:menu="handleMenuUpdate"
        >
            <template
                v-if="showSelectAll || $slots['prepend-item']"
                #prepend-item
            >
                <template v-if="showSelectAll">
                    <BListItem @click="toggleAll">
                        <BListItemTitle>
                            {{ allSelected ? t('table.deselectAll') : t('table.selectAll') }}
                        </BListItemTitle>
                    </BListItem>
                    <BDivider class="my-1" />
                </template>
                <slot name="prepend-item" />
            </template>
            <template #chip="{ item, index }">
                <BChip
                    v-if="index === 0"
                    size="x-small"
                    closable
                    class="b-filter-chip"
                    @click:close="removeItem(item.value)"
                >
                    {{ truncateTitle(item.title) }}
                </BChip>
                <BChip
                    v-else-if="index === 1"
                    size="x-small"
                    class="b-filter-chip b-filter-chip--count"
                >
                    +{{ selectedCount - 1 }}
                </BChip>
            </template>
            <template #no-data>
                <BListItem v-if="hasNoData">
                    <BListItemTitle>
                        {{ t('common.noData') }}
                    </BListItemTitle>
                </BListItem>
            </template>
        </BAutocomplete>
    </div>
</template>

<style scoped lang="scss">
.b-filter-group {
    display: flex;
    flex-direction: column;
    min-width: fit-content;
    flex-shrink: 0;
}

.b-filter-autocomplete {
    width: 250px;
    max-width: 100%;

    :deep(.v-field__input) {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 4px;
        padding-top: 4px;
        padding-bottom: 4px;
        min-height: 40px;
    }

    :deep(.v-chip) {
        margin: 0;
    }
}

.b-filter-chip {
    flex-shrink: 0;
}

.b-filter-chip--count {
    background-color: rgba(var(--v-theme-on-surface), 0.08);
    color: rgba(var(--v-theme-on-surface), 0.6);
}
</style>
